import { useMemo } from 'react';
import { Formik, Form } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { PodSecurityStandardsFormFields } from './PodSecurityStandardsFormFields';
import { PodSecurityStandardsFormValues } from './types';
import { usePodSecurityStandardsInitialValues } from './usePodSecurityStandardsInitialValues';
import { createPodSecurityStandardsValidationSchema } from './podSecurityStandardsValidationSchema';

interface Props {
  /** Form submission handler */
  onSubmit: (values: PodSecurityStandardsFormValues) => Promise<void>;
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Existing policy for edit mode - provides initial values */
  policy?: TypedPolicy;
  /** Template data for pre-filling the form in create mode */
  templateData?: Record<string, unknown>;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back is enabled - only used in create mode */
  canGoBack?: boolean;
}

export function PodSecurityStandardsPolicyForm({
  onSubmit,
  mode,
  policy,
  templateData,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = usePodSecurityStandardsInitialValues(
    policy,
    templateData
  );
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createPodSecurityStandardsValidationSchema(nameValidation),
    [nameValidation]
  );

  return (
    <Formik<PodSecurityStandardsFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <Form className="form-horizontal">
        <Widget>
          <WidgetBody>
            {mode === 'create' && (
              <PolicyConfigurationFormSection policyType="pod-security-standards-k8s" />
            )}
            <PodSecurityStandardsFormFields />
            {mode === 'edit' && (
              <PolicyFormFooter
                mode={mode}
                policyType="pod-security-standards-k8s"
                policyId={policy?.Id ?? null}
                originalEnvironmentGroupIds={originalEnvironmentGroupIds}
              />
            )}
          </WidgetBody>
        </Widget>

        {mode === 'create' && (
          <PolicyFormFooter
            mode={mode}
            policyType="pod-security-standards-k8s"
            policyId={policy?.Id ?? null}
            onBack={onBack}
            canGoBack={canGoBack}
          />
        )}
      </Form>
    </Formik>
  );
}
